// Crate (render 06 for layout; WI-13e mockup rw04): two halves, Shelf and Finds.
//  - Shelf: the collectibles a player's twists paid, one island's set at a time (chips per trip
//    island), as charms on a wooden shelf; ghosts for the ones still to find, gold at three.
//  - Finds: what you've found at real places, as toys in the crate's drawer.
// With the world stage live both are 3D on the stage (this screen leaves a slot for it, drives its
// filter glow and shows its hints); in Calm/Still or without WebGL they're flat.
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { useCrateView, type CrateView } from './crateView'
import { useTrips } from './PastTripsSheet'
import { shelfBooks, type ShelfTrip } from '../domain/bookShelf'
import type { Pack } from '../../shared/pack/schema'
import { openOverlay } from '../app/router'
import { useToast } from '../app/toast'
import type { Person } from '../../shared/events'
import { islandTitle } from '../arcade/Reward'
import { releaseTrip } from '../arcade/useArcade'
import { useChildSettings } from '../data/controls'
import { useEvents } from '../data/progress'
import { collectibleSet, setOf } from '../domain/collectibles'
import { crateToys, FILTER_TOYS } from '../domain/crateToys'
import { myPlaces } from '../domain/places'
import { playsOf } from '../domain/players'
import { GOLD_AT, ownedCollectibles } from '../domain/release'
import { useStage } from '../stage/bridge'
import type { ShelfCharm } from '../stage/sets/crateSet'
import { t } from '../strings/en'

/** The "Want to go" chip (t.crate.filters) lights the toys of wanted places. */
const WANT_FILTER = 4

/** The sets this player can collect: the trip's islands in stage order, then any other set they have one from. */
function shelfSets(pack: Pack, owned: ReadonlyMap<string, number>): string[] {
  const places = [...new Set(releaseTrip(pack).stages.map((s) => setOf(collectibleSet(s.place)[0]!.id)))]
  for (const id of owned.keys()) if (!places.includes(setOf(id))) places.push(setOf(id))
  return places
}

export function CrateScreen({ pack, person }: { pack: Pack; person: Person }) {
  const [filter, setFilter] = useState(0)
  // WI-34: 3D in every motion tier (Calm and Still get a still frame: crateSet.setTier).
  const stage3d = useStage((s) => s.status === 'live')
  const showToast = useToast((s) => s.show)
  const slot = useRef<HTMLDivElement>(null)
  const toys = useMemo(() => crateToys(pack), [pack])
  const events = useEvents()
  const mine = useMemo(() => myPlaces(events, person.id), [events, person.id])
  const settings = useChildSettings(person.kind === 'child' ? person : null)
  const canWant = settings?.canSuggest ?? true
  // A toy is found once any place behind it has been checked in at (F5).
  const foundToys = useMemo(() => new Set(toys.filter((x) => x.places.some((p) => mine.found.has(p.id))).map((x) => x.toy)), [toys, mine])
  const wantToys = useMemo(() => toys.filter((x) => x.places.some((p) => mine.wanted.has(p.id))).map((x) => x.toy), [toys, mine])
  const foundKey = [...foundToys].join(',')
  const lit = filter === WANT_FILTER ? wantToys : (FILTER_TOYS[filter] ?? null)
  const litKey = lit?.join(',') ?? 'all'

  // WI-13e: the shelf (each player's own collectibles).
  const player = playsOf(person)
  const owned = useMemo(() => ownedCollectibles(events, person.id), [events, person.id])
  const sets = useMemo(() => shelfSets(pack, owned), [pack, owned])
  const view = useCrateView((s) => s.view)
  const setView = (v: CrateView) => useCrateView.setState({ view: v })
  // B10: everyone has the trip books; the collectibles shelf is a player's.
  const mode: CrateView = player ? view : view === 'books' ? 'books' : 'finds'
  const activeTrip = pack.meta.id
  const trips = useTrips(mode === 'books')
  // The trip under way is always a book, even offline before the trips list has loaded.
  const books = useMemo(() => {
    const dates = { startDate: pack.meta.startDate, endDate: pack.meta.endDate }
    const list: ShelfTrip[] = (trips ?? []).map((x) => (x.id === activeTrip && !x.app ? { ...x, app: dates } : x))
    if (!list.some((x) => x.id === activeTrip)) list.push({ id: activeTrip, title: pack.meta.title, app: { startDate: pack.meta.startDate, endDate: pack.meta.endDate } })
    return shelfBooks(list, activeTrip)
  }, [trips, activeTrip, pack.meta])
  const booksKey = books.map((b) => `${b.id}:${b.title}:${b.year}:${b.colour}:${b.now ? 1 : 0}`).join(',')
  const openBook = (id: string) => openOverlay(`trip-book:${id}`)
  const [setId, setSetId] = useState<string | null>(null)
  const shown = setId && sets.includes(setId) ? setId : (sets[0] ?? '')
  const charms: ShelfCharm[] = useMemo(
    () =>
      collectibleSet(shown).map((c) => {
        const n = owned.get(c.id) ?? 0
        return { id: c.id, emoji: c.emoji, ...(c.art ? { art: c.art } : {}), state: n >= GOLD_AT ? 'gold' : n ? 'solid' : 'ghost' }
      }),
    [shown, owned],
  )
  const charmKey = charms.map((c) => `${c.id}:${c.state}`).join(',')
  const have = (set: string) => collectibleSet(set).filter((c) => (owned.get(c.id) ?? 0) > 0).length

  // The 3D crate or shelf: which toys are found, the filter, the charms and its slot.
  useLayoutEffect(() => {
    if (!stage3d) return
    const measure = () => {
      const r = slot.current?.getBoundingClientRect()
      if (!r) return
      useStage.getState().set({
        crate: { frame: { top: r.top, bottom: r.bottom }, toys: toys.map((x) => ({ toy: x.toy, found: foundToys.has(x.toy) })), lit, mode, shelf: charms, books, nowLabel: t.crate.booksNow },
      })
    }
    measure()
    const ro = new ResizeObserver(measure)
    if (slot.current) ro.observe(slot.current)
    window.addEventListener('resize', measure)
    return () => {
      ro.disconnect()
      window.removeEventListener('resize', measure)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- keyed on foundKey/litKey/charmKey, not the new Set/array each render
  }, [stage3d, toys, foundKey, litKey, mode, charmKey, booksKey])
  useEffect(() => {
    useStage.getState().set({
      crateEvents: {
        onHint: (toy) => {
          const names = toys.find((x) => x.toy === toy)?.places.map((p) => p.name) ?? []
          if (names.length) showToast(t.crate.hint(names), 3200)
        },
        onBook: (id) => openOverlay(`trip-book:${id}`),
      },
    })
    return () => useStage.getState().set({ crateEvents: { onHint: () => {} } })
  }, [toys, showToast])

  const shelfSet = collectibleSet(shown)
  const left = shelfSet.length - have(shown)

  return (
    <main className={`screen crate${stage3d ? ' on-stage' : ''}`}>
      <div className="screen-head">
        <h1 className="h1">{t.crate.title}</h1>
        <span className="head-pills">
          {mode === 'shelf' ? (
            <span className="pill">{t.crate.collected(owned.size)}</span>
          ) : (
            <span className="pill">{t.crate.counts(mine.found.size, mine.wanted.size)}</span>
          )}
          <button className="pill press" aria-label={t.past.title} onClick={() => openOverlay('past-trips')}>
            🧳
          </button>
        </span>
      </div>
      <div className="crate-tabs" role="tablist" aria-label={t.crate.title}>
        {player ? (
          <button role="tab" aria-selected={mode === 'shelf'} className={`press${mode === 'shelf' ? ' on' : ''}`} onClick={() => setView('shelf')}>
            {t.crate.shelfTab}
          </button>
        ) : null}
        <button role="tab" aria-selected={mode === 'finds'} className={`press${mode === 'finds' ? ' on' : ''}`} onClick={() => setView('finds')}>
          {t.crate.findsTab(mine.found.size)}
        </button>
        <button role="tab" aria-selected={mode === 'books'} className={`press${mode === 'books' ? ' on' : ''}`} onClick={() => setView('books')}>
          {t.crate.booksTab}
        </button>
      </div>
      {mode === 'books' ? (
        <>
          {stage3d ? (
            <>
              <div ref={slot} className="crate-slot" role="img" aria-label={t.crate.booksLabel(books.length)} />
              {/* §8.3: the 3D books are a picture; a keyboard or screen reader opens them from this list
                  (hidden until it has focus). */}
              <div className="sr-only-focusable" role="list" aria-label={t.crate.booksLabel(books.length)}>
                {books.map((b) => (
                  <div role="listitem" key={b.id}>
                    <button className="chip press" onClick={() => openBook(b.id)}>
                      {t.crate.bookSpine(b.title, b.year, b.now)}
                    </button>
                  </div>
                ))}
              </div>
            </>
          ) : (
            // Calm/Still or no WebGL: the same books as flat spines.
            <div className="book-shelf-flat" role="list" aria-label={t.crate.booksLabel(books.length)}>
              {books.map((b) => (
                // A list item holding a button (a button can't be the list item: its role would go).
                <div role="listitem" key={b.id} className="book-spine-item">
                  <button className="book-spine press" style={{ background: b.colour }} aria-label={t.crate.bookSpine(b.title, b.year, b.now)} onClick={() => openBook(b.id)}>
                    {b.now ? <i className="book-now">{t.crate.booksNow}</i> : null}
                    {b.year ? <span className="book-year">{b.year}</span> : null}
                    <b>{b.title}</b>
                    <span className="book-mark" aria-hidden>
                      {b.mark}
                    </span>
                  </button>
                </div>
              ))}
              <div role="listitem" className="book-spine ghost" aria-label={t.crate.booksNext}>
                ?
              </div>
            </div>
          )}
          <p className="crate-books-note">
            {trips === null ? t.crate.booksLoading : `${t.crate.booksCaption} ${t.crate.booksNext}`}
          </p>
          {books[0] ? (
            <div className="dock-action">
              <button className="btn y press" onClick={() => openBook(books[0]!.id)}>
                {t.crate.openBook(books[0].title, books[0].year)}
              </button>
            </div>
          ) : null}
        </>
      ) : mode === 'shelf' ? (
        <>
          <div className="filters" role="tablist" aria-label={t.crate.sets}>
            {sets.map((s) => (
              <button key={s} role="tab" aria-selected={s === shown} className={`pill press${s === shown ? ' pill-ink' : ''}`} onClick={() => setSetId(s)}>
                {t.crate.setChip(islandTitle(s), have(s), collectibleSet(s).length)}
              </button>
            ))}
          </div>
          {stage3d ? (
            <div ref={slot} className="crate-slot" role="img" aria-label={t.crate.shelfLabel(islandTitle(shown), have(shown), shelfSet.length)} />
          ) : (
            <div className="shelf-flat" role="list" aria-label={t.crate.shelfLabel(islandTitle(shown), have(shown), shelfSet.length)}>
              {charms.map((c, i) => (
                <div role="listitem" key={c.id} className={`charm ${c.state}`} aria-label={c.state === 'ghost' ? t.crate.notYet : shelfSet[i]!.name}>
                  {c.state === 'ghost' ? '?' : c.art ? <img src={c.art} alt="" /> : c.emoji}
                </div>
              ))}
            </div>
          )}
          <div className="panel crate-set-note">
            <span aria-hidden>🎁</span>
            <div>
              <b>{left ? t.crate.finishSet(islandTitle(shown)) : t.crate.setComplete(islandTitle(shown))}</b>
              <small>{left ? t.crate.toGo(left) : t.crate.goldNote}</small>
            </div>
          </div>
        </>
      ) : (
        <>
          {/* WI-31: the filters stay hidden until something is found or wanted. */}
          <div className="filters" role="tablist" hidden={!mine.found.size && !mine.wanted.size}>
            {t.crate.filters.map((f, i) => (
              <button key={f} role="tab" aria-selected={i === filter} className={`pill press${i === filter ? ' pill-ink' : ''}`} onClick={() => setFilter(i)}>
                {f}
              </button>
            ))}
          </div>
          {stage3d ? (
            <>
              <div ref={slot} className="crate-slot" role="img" aria-label={`Crate: ${toys.length} toys still to find. Tap one for a hint.`} />
              {/* §8.3: each 3D toy's hint, for a keyboard or screen reader (hidden until focused). */}
              <div className="sr-only-focusable" role="list" aria-label={t.crate.ghostsLabel(toys.length)}>
                {toys.map((x) => (
                  <div role="listitem" key={x.toy}>
                    <button className="chip press" onClick={() => showToast(t.crate.hint(x.places.map((p) => p.name)), 3200)}>
                      {t.crate.hint(x.places.map((p) => p.name))}
                    </button>
                  </div>
                ))}
              </div>
              <p className="crate-caption">{t.crate.stageCaption}</p>
            </>
          ) : mine.found.size ? (
            // Calm/Still: the toys found so far as a plain list (the 3D crate shows them as toys).
            <div className="panel sheet-panel crate-found" role="list" aria-label={t.crate.foundList}>
              {pack.places
                .filter((p) => mine.found.has(p.id))
                .map((p) => (
                  <div className="row" role="listitem" key={p.id}>
                    <span className="fam-av">✅</span>
                    <div>
                      <b>{p.name}</b>
                      <small>{p.summary}</small>
                    </div>
                  </div>
                ))}
            </div>
          ) : (
            <EmptyDrawer toys={toys} />
          )}
          {/* WI-31: with nothing found yet, Ideas is the call to action. */}
          <div className="dock-action two">
            <button className={`btn ${mine.found.size ? 'w' : 'y'} press`} onClick={() => openOverlay('ideas')}>
              {t.crate.ideas(pack.places.length)}
            </button>
            <button className={`btn ${mine.found.size ? 'y' : 'w'} press`} disabled={!canWant} style={{ fontSize: 17 }} onClick={() => openOverlay('want')}>
              {t.crate.want}
            </button>
          </div>
        </>
      )}
    </main>
  )
}

/** A friendly stand-in picture for each kind of toy (the 3D crate draws the toys themselves). */
const TOY_EMOJI: Record<string, string> = { ramen: '🍜', bucket: '♨️', chair: '🚡', float: '🔮', can: '🥫', snowman: '⛄', train: '🚃', tower: '🗼' }

/**
 * WI-31: the empty Crate (Calm/Still or no WebGL). Not a blank drawer: the toys this trip can fill
 * it with, as ghost tiles, each with where to find it, and a line on how they turn up.
 */
function EmptyDrawer({ toys }: { toys: { toy: string; places: { name: string }[] }[] }) {
  return (
    <div className="crate-empty">
      <div className="crate-empty-head">
        <b>{t.crate.emptyTitle}</b>
        {t.crate.emptyLines.map((line) => (
          <small key={line}>{line}</small>
        ))}
      </div>
      <div className="ghost-tiles" role="list" aria-label={t.crate.ghostsLabel(toys.length)}>
        {toys.map((x) => (
          <div className="ghost-tile" role="listitem" key={x.toy} aria-label={t.crate.hint(x.places.map((p) => p.name))}>
            <span aria-hidden>{TOY_EMOJI[x.toy] ?? '❔'}</span>
            <i aria-hidden>?</i>
            <small>{x.places[0]?.name ?? ''}</small>
          </div>
        ))}
      </div>
    </div>
  )
}
